import React from 'react';
import WideView from 'src/components/atomic/layouts/wideView/WideView';
import Card from 'src/components/atomic/atoms/card/Card';
import { BUDGET_OVERALL_ID } from 'src/services/budget.service';
import { LocalDate } from '@js-joda/core';
import { useUser } from 'src/hooks/queries/users';
import SpendingHistoryBarChart from 'src/components/atomic/molecules/spendingHistoryBarChart/SpendingHistoryBarChart';
import LiteralsService from 'src/services/literals/LiteralsService';
import { BudgetHistoryViewModel } from 'src/types/view-models/budgets/budget-history.view.model';
import BudgetHeaderRow from 'src/components/budgets/budget-header-row/BudgetHeaderRow';
import { useHistory } from 'react-router-dom';
import { UserUtils } from 'src/types/user.model';
import { useSpendSmartDocumentTitle } from 'src/hooks/useDocumentTitle';
import { goToBudgetCategoryTransactions } from 'src/services/router.service';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { useTrackPage } from 'src/hooks/useAdobeTags';
import { PageTimePeriod } from 'src/types/page-time-period.enum';
import PageOrientation from 'src/components/atomic/molecules/pageOrientation/PageOrientation';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import SpendingSumGrid from 'src/components/atomic/organisms/spendingSumGrid/SpendingSumGrid';
import LinkExternalAccountCallout from 'src/components/atomic/molecules/linkExternalAccountCallout/LinkExternalAccountCallout';
import budgetStyles from 'src/components/atomic/pages/budgets/budgetStyles.module.scss';
import { useFeatureFlag } from 'src/hooks/queries/one-xp';
import oneXP from 'src/types/one-xp';
import YearlySpending from 'src/components/atomic/layouts/spendingBudget/YearlySpending';

export default function BudgetHistoryView({
    propHandleOpenTransactions,
    propHistory,
    handleBack
}: BudgetHistoryViewModel): JSX.Element {
    const history = useHistory();
    const { data: user } = useUser();
    useSpendSmartDocumentTitle(LiteralsService.getLiteral('Budget.titles.history'));
    useTrackPage(LiteralsService.getLiteral('Budget.titles.history'));
    const { data: categoryCardsRefreshPhase1FeatureFlag } = useFeatureFlag(
        oneXP.FeatureFlags.CategoryCardsRefreshPhase1
    );
    const isFullPlanUser: boolean = !UserUtils.isBudgetingUser(user);

    const handleOpenTransactions = (startDate: LocalDate, endDate: LocalDate): void => {
        // TODO: remove when refactor of Board has been complete
        if (isFullPlanUser) {
            propHandleOpenTransactions(BUDGET_OVERALL_ID, startDate, endDate);
        } else {
            goToBudgetCategoryTransactions(false, history, BUDGET_OVERALL_ID, startDate, endDate);
        }
    };

    return (
        <WideView
            title={LiteralsService.getLiteral('Budget.title')}
            hasBackgroundBanner
            showFooter
            hideBackButton={!isFullPlanUser}
            history={propHistory}
            handleBack={handleBack}
            additionalHeaderRows={<BudgetHeaderRow currentPage={BudgetPages.HISTORY} />}
            withDocTitle={false}
            data-cy={Selectors.SpendSmart.HISTORY_ROOT}
        >
            <Card showBackgroundBanner className={budgetStyles.cardBody}>
                <PageOrientation timePeriod={PageTimePeriod.LAST_YEAR} propHistory={propHistory} />
                <SpendingHistoryBarChart
                    title={LiteralsService.getLiteral('Budget.Chart.generalBarChartTitle')}
                    handleOpenTransactions={handleOpenTransactions}
                    tagName={adobeTags.History.HistoryGraph}
                />
                <div>
                    {categoryCardsRefreshPhase1FeatureFlag ? (
                        <YearlySpending />
                    ) : (
                        <>
                            <SpendingSumGrid timePeriod={PageTimePeriod.LAST_YEAR} />
                            <LinkExternalAccountCallout />
                        </>
                    )}
                </div>
            </Card>
        </WideView>
    );
}
